import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Alert,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { useEffect, useState } from "react";
import api from '../../api';

export default function NewObjectivesSection({ newObjectives, setNewObjectives, onNextStep }) {
  const [errors, setErrors] = useState([]);
  const [erroresSMART, setErroresSMART] = useState([]);

  // Redistribuye el peso automáticamente si no se modificó manualmente
  const ajustarPesos = (objetivos, modificadoIndex, modificadoManual) => {
    const totalFijado = objetivos.reduce((acc, obj, i) =>
      i === modificadoIndex && modificadoManual ? acc + parseFloat(obj.weight || 0) : acc, 0);
    const restantes = objetivos.length - (modificadoManual ? 1 : 0);
    if (restantes <= 0) return;

    const pesoRestante = 100 - totalFijado;
    const actualizados = objetivos.map((obj, i) => {
      if (i === modificadoIndex && modificadoManual) return obj;
      return { ...obj, weight: Math.floor(pesoRestante / restantes) };
    });
    setNewObjectives(actualizados);
  };

  const handleAddObjective = () => {
    setNewObjectives([
      ...newObjectives,
      { description: "", weight: null, date: "", plan: "" },
    ]);
  };

  const handleRemoveObjective = (index) => {
    const updated = [...newObjectives];
    updated.splice(index, 1);
    setNewObjectives(updated);
  };

  const handleChange = (index, field, value) => {
    const updated = [...newObjectives];
    updated[index][field] = field === "weight" ? Number(value) : value;
    setNewObjectives(updated);
    ajustarPesos(updated, index, field === "weight");
  };

  const validateAll = async () => {
    const today = new Date().toISOString().split("T")[0];
    const totalWeight = newObjectives.reduce((sum, o) => sum + (parseInt(o.weight) || 0), 0);
    const errorMessages = [];

    try {
      const res = await api.post("/api/validate-smart-objectives", {
        objectives: newObjectives.map(o => o.description),
      });
      const validados = res.data.validation;
      const noSMART = validados.filter(v => !v.isSMART);
      setErroresSMART(noSMART);
      noSMART.forEach((e, i) => {
        errorMessages.push(`Objetivo ${i + 1}: ${e.reason}. Sugerencia: ${e.suggestion}`);
      });
    } catch (err) {
      console.error("Error en validación SMART", err);
      errorMessages.push("No se pudo validar si los objetivos son SMART.");
    }

    newObjectives.forEach((obj, i) => {
      if (!obj.plan || obj.plan.trim().length < 5) {
        errorMessages.push(`Objetivo ${i + 1}: el plan debe tener al menos 5 caracteres.`);
      }
      if (!obj.date || obj.date <= today) {
        errorMessages.push(`Objetivo ${i + 1}: la fecha debe ser posterior a hoy.`);
      }
    });

    if (totalWeight !== 100) {
      errorMessages.push(`La suma de los pesos debe ser 100%. Actualmente suma ${totalWeight}%.`);
    }

    setErrors(errorMessages);
    return errorMessages.length === 0;
  };

  return (
    <Box mb={4}>
      <Typography variant="h6" gutterBottom>
        Nuevos Objetivos para el Próximo Período
      </Typography>

      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.map((e, i) => (
            <div key={i}>{e}</div>
          ))}
        </Alert>
      )}

      {erroresSMART.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {erroresSMART.map((err, i) => (
            <div key={i}>
              <strong>Objetivo:</strong> {err.original} <br />
              <strong>Motivo:</strong> {err.reason} <br />
              <strong>Sugerencia:</strong> {err.suggestion}
            </div>
          ))}
        </Alert>
      )}

      {newObjectives.map((objective, index) => (
        <Box
          key={index}
          display="flex"
          flexDirection="column"
          gap={2}
          mb={3}
          p={2}
          border="1px solid #ccc"
          borderRadius={2}
        >
          <TextField
            label={`Objetivo ${index + 1}`}
            value={objective.description}
            onChange={(e) => handleChange(index, "description", e.target.value)}
            fullWidth
            required
          />
          <Box display="flex" gap={2}>
            <TextField
              label="Fecha de vencimiento"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={objective.date}
              onChange={(e) => handleChange(index, "date", e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Peso del objetivo"
              type="number"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              value={objective.weight ?? ""}
              onChange={(e) => handleChange(index, "weight", e.target.value)}
              required
              fullWidth
            />
          </Box>
          <TextField
            label="Plan de Acción"
            value={objective.plan}
            onChange={(e) => handleChange(index, "plan", e.target.value)}
            fullWidth
            required
            multiline
            minRows={2}
          />
          <Box mt={1}>
            <IconButton color="error" onClick={() => handleRemoveObjective(index)}>
              <Delete />
            </IconButton>
          </Box>
        </Box>
      ))}

      <Button variant="outlined" startIcon={<Add />} onClick={handleAddObjective}>
        Agregar Objetivo
      </Button>

      <Button
        variant="contained"
        color="primary"
        sx={{ mt: 2, ml: 2 }}
        onClick={async () => {
          const valido = await validateAll();
          if (valido) {
            onNextStep();
          }
        }}
      >
        Continuar
      </Button>
    </Box>
  );
}
